/**
 * Główny hook zaawansowanych filtrów i widoczności kolumn.
 * Składa sub-hooki (Redux, reguły filtrów, widoczność/kolejność, popper, zapis, presety),
 * effecty synchronizacji przy otwarciu dialogu i zakładce „Widoczność kolumn”,
 * oraz zwraca jeden interfejs dla AdvancedFiltersDialog i WiseTable.
 */

import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Options } from 'react-select';
import { useDispatch } from 'store';
import { reduxActions } from 'store';
import type {
  IWiseTableColumn,
  IWiseTableFilter,
  IOption,
  TWiseTableAdvancedFilterOperator
} from 'components/wise/types';
import {
  ADVANCED_FILTER_OPERATOR_OPTIONS_COMPARISON,
  LOGICAL_CONNECTOR_OPTIONS,
  getEmptyFilterRow
} from 'components/wise/WiseTable/constants';
import { filtersToGroups } from 'components/wise/WiseTable/utils/filterGroups';

import type { IUseWiseTableAdvancedFiltersParams, IUseWiseTableAdvancedFiltersReturn } from './types';
import { useReduxState } from './useReduxState';
import { useFilterRules } from './useFilterRules';
import { useColumnVisibilityAndOrder } from './useColumnVisibilityAndOrder';
import { useColumnFilterPopper } from './useColumnFilterPopper';
import { useSaveFiltersDialog } from './useSaveFiltersDialog';
import { useSavedSettingsPresets } from './useSavedSettingsPresets';

export type { IUseWiseTableAdvancedFiltersParams, IUseWiseTableAdvancedFiltersReturn, ISavedSettingsOptionItem } from './types';

export const useWiseTableAdvancedFilters = ({
  columns,
  parameters,
  filters = [],
  pageSymbol,
  componentSymbol
}: IUseWiseTableAdvancedFiltersParams): IUseWiseTableAdvancedFiltersReturn => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [advancedFiltersDialogOpen, setAdvancedFiltersDialogOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [showColumnFilterIcons, setShowColumnFilterIcons] = useState(true);
  const [pendingSelectedPresetId, setPendingSelectedPresetId] = useState<string | null>(null);

  const redux = useReduxState({ componentSymbol, pageSymbol });
  const {
    savedFilters,
    columnVisibility,
    columnOrder,
    selectedSettingsPresetName,
    selectedSettingsPresetIdFromRedux,
    isDynamicUI
  } = redux;

  const filterRules = useFilterRules({
    savedFilters,
    isDynamicUI,
    pageSymbol,
    componentSymbol
  });
  const {
    filterRuleGroups,
    setFilterRuleGroups,
    addRule,
    addConditionInGroup,
    removeRow,
    updateRow,
    applyColumnFilter,
    getColumnFilterRowsForColumn,
    buildFiltersFromRows,
    hasAnyCompleteRow
  } = filterRules;

  const columnVisibilityOrder = useColumnVisibilityAndOrder({
    columnVisibility,
    columnOrder,
    columns,
    isDynamicUI,
    pageSymbol,
    componentSymbol
  });
  const {
    pendingColumnVisibility,
    setPendingColumnVisibility,
    pendingColumnOrder,
    setPendingColumnOrderState,
    setPendingColumnOrder,
    onColumnVisibilityChange,
    handleColumnVisibilityToggle,
    applyColumnVisibility,
    hasPendingColumnVisibilityChange
  } = columnVisibilityOrder;

  const popper = useColumnFilterPopper(showColumnFilterIcons);

  const saveDialog = useSaveFiltersDialog({
    buildFiltersFromRows,
    pendingColumnVisibility,
    pendingColumnOrder,
    selectedSettingsPresetIdFromRedux,
    componentSymbol,
    isDynamicUI,
    pageSymbol,
    applyColumnVisibility
  });

  const savedPresets = useSavedSettingsPresets({
    componentSymbol,
    advancedFiltersDialogOpen,
    isDynamicUI,
    pageSymbol,
    setFilterRuleGroups,
    setPendingColumnVisibility,
    setPendingColumnOrderState,
    setPendingSelectedPresetId
  });
  const {
    savedSettingsOptionsForSelect,
    handleSelectSettingsPreset,
    handleApplySettingsPreset: handleApplySettingsPresetInner,
    handleClearSavedFilters
  } = savedPresets;

  const hasInitializedFromRedux = useRef(false);
  const prevAdvancedFiltersDialogOpen = useRef(false);

  useEffect(() => {
    if (savedFilters.length > 0 && !hasInitializedFromRedux.current) {
      hasInitializedFromRedux.current = true;
      const groups = filtersToGroups(savedFilters);
      setFilterRuleGroups(groups.length > 0 ? groups : [[getEmptyFilterRow()]]);
    }
  }, [savedFilters, setFilterRuleGroups]);

  useEffect(() => {
    const justOpened =
      advancedFiltersDialogOpen && !prevAdvancedFiltersDialogOpen.current;
    prevAdvancedFiltersDialogOpen.current = advancedFiltersDialogOpen;
    if (justOpened) {
      setPendingSelectedPresetId(selectedSettingsPresetName);
      const groups = filtersToGroups(savedFilters);
      setFilterRuleGroups(groups.length > 0 ? groups : [[getEmptyFilterRow()]]);
      setPendingColumnVisibility(columnVisibility);
      const order =
        columnOrder.length > 0 ? columnOrder : columns.map((c) => c.field_symbol);
      setPendingColumnOrderState(order);
    }
  }, [
    advancedFiltersDialogOpen,
    selectedSettingsPresetName,
    savedFilters,
    columnVisibility,
    columnOrder,
    columns,
    setFilterRuleGroups,
    setPendingColumnVisibility,
    setPendingColumnOrderState
  ]);

  const prevTabIndexRef = useRef<number>(0);
  useEffect(() => {
    if (advancedFiltersDialogOpen && tabIndex === 1 && prevTabIndexRef.current !== 1) {
      setPendingColumnVisibility(columnVisibility);
      const order =
        columnOrder.length > 0 ? columnOrder : columns.map((c) => c.field_symbol);
      setPendingColumnOrderState(order);
    }
    prevTabIndexRef.current = tabIndex;
  }, [advancedFiltersDialogOpen, tabIndex, columnVisibility, columnOrder, columns, setPendingColumnVisibility, setPendingColumnOrderState]);

  const getFilterByFieldSymbol = useCallback(
    (fieldSymbol: string | null): IWiseTableFilter | null => {
      if (!fieldSymbol) return null;
      const fromFilters = filters.find((f) => f.field_symbol === fieldSymbol);
      if (fromFilters) return fromFilters;
      const param = parameters.find((p) => p.field_symbol === fieldSymbol);
      return (param as IWiseTableFilter) ?? null;
    },
    [filters, parameters]
  );

  const columnOptions: Options<IOption<IWiseTableColumn>> = useMemo(
    () =>
      columns.map((col) => ({
        value: col.field_symbol,
        label: col.label,
        item: col
      })),
    [columns]
  );

  const operatorOptions: Options<IOption<{ value: TWiseTableAdvancedFilterOperator }>> = useMemo(
    () =>
      ADVANCED_FILTER_OPERATOR_OPTIONS_COMPARISON.map((op) => ({
        value: op.value,
        label: t(op.labelKey),
        item: { value: op.value }
      })),
    [t]
  );

  const connectorOptions: Options<IOption<{ value: 'and' | 'or' }>> = useMemo(
    () =>
      LOGICAL_CONNECTOR_OPTIONS.map((op) => ({
        value: op.value,
        label: t(op.labelKey),
        item: { value: op.value }
      })),
    [t]
  );

  const applyFiltersToRedux = useCallback(() => {
    const filtersToDispatch = buildFiltersFromRows();
    if (isDynamicUI && pageSymbol) {
      dispatch(
        reduxActions.setWiseTableAdvancedFilters({
          pageSymbol,
          componentSymbol,
          filters: filtersToDispatch
        })
      );
    } else {
      dispatch(
        reduxActions.setTraderPanelWiseTableAdvancedFilters({
          componentSymbol,
          filters: filtersToDispatch
        })
      );
    }
    setAdvancedFiltersDialogOpen(false);
  }, [buildFiltersFromRows, dispatch, isDynamicUI, pageSymbol, componentSymbol]);

  const clearAllFilters = useCallback(() => {
    setFilterRuleGroups([[getEmptyFilterRow()]]);
    if (isDynamicUI && pageSymbol) {
      dispatch(
        reduxActions.setWiseTableAdvancedFilters({
          pageSymbol,
          componentSymbol,
          filters: []
        })
      );
    } else {
      dispatch(
        reduxActions.setTraderPanelWiseTableAdvancedFilters({
          componentSymbol,
          filters: []
        })
      );
    }
  }, [dispatch, isDynamicUI, pageSymbol, componentSymbol, setFilterRuleGroups]);

  const handleApplySettingsPreset = useCallback(() => {
    handleApplySettingsPresetInner(pendingSelectedPresetId);
  }, [handleApplySettingsPresetInner, pendingSelectedPresetId]);

  const getColumnLabel = useCallback(
    (fieldSymbol: string) =>
      columns.find((c) => c.field_symbol === fieldSymbol)?.label ?? fieldSymbol,
    [columns]
  );

  return {
    advancedFiltersDialogOpen,
    onOpenAdvancedFiltersDialog: () => setAdvancedFiltersDialogOpen(true),
    onCloseAdvancedFiltersDialog: () => setAdvancedFiltersDialogOpen(false),
    columnVisibility,
    pendingColumnVisibility,
    onColumnVisibilityChange,
    applyColumnVisibility,
    tabIndex,
    setTabIndex,
    filterRuleGroups,
    selectedSettingsPresetName,
    pendingSelectedPresetId,
    saveNameDialogType: saveDialog.saveNameDialogType,
    saveNameValue: saveDialog.saveNameValue,
    setSaveNameValue: saveDialog.setSaveNameValue,
    overwritePresetWhenSaving: saveDialog.overwritePresetWhenSaving,
    setOverwritePresetWhenSaving: saveDialog.setOverwritePresetWhenSaving,
    columnOptions,
    operatorOptions,
    connectorOptions,
    getFilterByFieldSymbol,
    addRule,
    addConditionInGroup,
    removeRow,
    updateRow,
    handleOpenSaveFiltersNameDialog: saveDialog.handleOpenSaveFiltersNameDialog,
    handleOpenSaveSettingsNameDialog: saveDialog.handleOpenSaveSettingsNameDialog,
    handleCloseSaveNameDialog: saveDialog.handleCloseSaveNameDialog,
    handleConfirmSaveName: saveDialog.handleConfirmSaveName,
    applyFiltersToRedux,
    clearAllFilters,
    hasAnyCompleteRow,
    handleColumnVisibilityToggle,
    hasPendingColumnVisibilityChange,
    columnOrder,
    pendingColumnOrder,
    setPendingColumnOrder,
    savedFilters,
    handleClearSavedFilters,
    savedSettingsOptionsForSelect,
    handleSelectSettingsPreset,
    handleApplySettingsPreset,
    getColumnLabel,
    columnFilterPopperOpen: popper.columnFilterPopperOpen,
    columnFilterPopperColumnId: popper.columnFilterPopperColumnId,
    columnFilterPopperAnchorEl: popper.columnFilterPopperAnchorEl,
    onOpenColumnFilterDialog: popper.onOpenColumnFilterDialog,
    onCloseColumnFilterDialog: popper.onCloseColumnFilterDialog,
    applyColumnFilter,
    getColumnFilterRowsForColumn,
    showColumnFilterIcons,
    onToggleColumnFilterIcons: () => setShowColumnFilterIcons((prev) => !prev)
  };
};
